// STEP 1'S RULES — each the app's, each cited (WEB-PARITY-1C). Pure: what `Details.tsx` shows and sends is decided here,
// from the server's subject and decisions, so a test can reach every rule without a page.

import type { Copy, Money, ResolvePropertyResponse, SubjectView } from "@contract";
import { longDate, money } from "../../format";
import type { Corrections } from "../../app/corrections";
import { isBlocked, type AnyDecision } from "../../transport/types";
import { words, wordsWith, type DetailsWordingId } from "./detailsWording";

type Decisions = ResolvePropertyResponse["decisions"];

// ── the rules, each the app's ─────────────────────────────────────────────────────────────────────────────────────────

/** The decisions `/details` already renders on its own, so a block among them is not said twice. */
export const SHOWN_BELOW: ReadonlySet<string> = new Set(["P-1", "NEW-2", "NEW-3", "I-2"]);

/**
 * THE DECISIONS WHOSE BLOCK REFUSES THE RECORD, NOT THE APPEAL. The record is another property's (I-2, I-4/I-5) or the
 * whole building's (NEW-1), so none of its figures are used and the subject is built from the owner's own. Their block
 * stands on every later lookup, because the record does not change, so it cannot be what holds the owner here: what holds
 * them is what judges the figures they enter instead — P-1, NEW-3 and G-4.
 */
export const RECORD_REFUSALS: ReadonlySet<string> = new Set(["I-2", "I-4/I-5", "NEW-1"]);

/** Every decision on the record's answer that ran and blocked, with its sentence — read off the decisions themselves. */
export function blockingDecisions(decisions: Decisions): { id: string; copy: Copy | null }[] {
  const out: { id: string; copy: Copy | null }[] = [];
  for (const [id, decision] of Object.entries(decisions) as [string, AnyDecision][]) {
    if (isBlocked(decision) && decision.ran) out.push({ id, copy: decision.copy });
  }
  return out;
}

export function ranBlock(decision: AnyDecision): boolean {
  return decision.ran && decision.outcome === "block";
}

export function ranWarn(decision: AnyDecision): boolean {
  return decision.ran && decision.outcome === "warn";
}

export type Fact<V> = { value: V; source: string; recordValue?: V | null } | null | undefined;

/**
 * THE OWNER-ENTERED PATH (`factsAreOwnerEntered`, Models.swift:260): no record was adopted, so every figure is the
 * owner's own. Read two ways, either enough: the server refused the record (I-2, I-4/I-5, NEW-1 blocking — it adopts
 * none of those, `resolve/record.ts` step 7), or nothing on the subject came from a record. The property type does not
 * count: it is the owner's pick on both paths.
 */
export function factsAreOwnerEntered(subject: SubjectView, decisions: Decisions): boolean {
  const refused = [...RECORD_REFUSALS].some((id) => ranBlock(decisions[id as keyof Decisions] as AnyDecision));
  if (refused) return true;
  const facts: Fact<unknown>[] = [
    subject.parcel, subject.assessedValue, subject.marketValue, subject.sqft, subject.beds, subject.baths,
    subject.yearBuilt, subject.lotSizeSqft, subject.assessedYear,
  ];
  return !facts.some((f) => f !== null && f !== undefined && f.source !== "ownerEntered");
}

/** The county's own figure for a fact: the record's value beside a correction, or the fact itself when it is the record's. */
export function countyFigure<V>(fact: Fact<V>): V | null {
  if (fact === null || fact === undefined) return null;
  if (fact.source === "ownerCorrected") return fact.recordValue ?? null;
  if (fact.source === "provider" || fact.source === "county") return fact.value;
  return null;
}

export type Correct = <K extends keyof Corrections>(key: K, value: Corrections[K] | undefined) => void;
export type NumberKey = "sqft" | "beds" | "baths" | "yearBuilt" | "lotSizeSqft";

/**
 * AN EDIT, AS A CORRECTION. A figure equal to the county's own — or an emptied field — is no correction: the key is left
 * out, and the county's figure stands (or, with no record, the field is empty). Anything else is the owner's figure.
 */
export function commitNumber(key: NumberKey, typed: number | null, fact: Fact<number>, correct: Correct): void {
  if (typed === (fact?.value ?? null)) return;
  if (typed === null || typed === countyFigure(fact)) correct(key, undefined);
  else correct(key, typed);
}

export function commitAssessed(typed: number | null, subject: SubjectView, correct: Correct): void {
  const fact = subject.assessedValue;
  if (typed === (fact?.value.amount ?? null)) return;
  const county = countyFigure<Money<"assessed">>(fact)?.amount ?? null;
  if (typed === null || typed === county) correct("assessedValue", undefined);
  else correct("assessedValue", { basis: "assessed", amount: typed } as Money<"assessed">);
}

export function commitText(key: "parcel", typed: string, fact: Fact<string>, correct: Correct): void {
  const value = typed.trim();
  if (value === (fact?.value ?? "").trim()) return;
  if (value === "" || value === countyFigure(fact)) correct(key, undefined);
  else correct(key, value);
}

/** `sqftCorrection` (Models.swift:666-671): the record's figure beside the owner's, when they differ by more than 20 sq ft
 *  AND more than 2%. */
export function sqftCorrection(fact: Fact<number>): { record: number; corrected: number } | null {
  if (fact === null || fact === undefined || fact.source !== "ownerCorrected") return null;
  const record = fact.recordValue ?? null;
  const corrected = fact.value;
  if (record === null || !(record > 0) || !(corrected > 0)) return null;
  const diff = Math.abs(record - corrected);
  if (!(diff > 20 && diff / record > 0.02)) return null;
  return { record, corrected };
}

/**
 * THE SENTENCE THAT EXPLAINS THE GATE (`completionRequirementSentence`, Models.swift:1044-1059), shown while G-4 says the
 * subject is not minimally complete — the server's `isMinimallyComplete` (Models.swift:922), which is also what keeps
 * Continue off. Named per field, in the app's order; the parcel only where the facts are the owner's own
 * (`parcelIsRequired`, Models.swift:944).
 */
export function requirementSentence(
  subject: SubjectView,
  g4: Decisions["G-4"],
  ownerEntered: boolean,
): { id: DetailsWordingId; text: string; missing: string } | null {
  if (!g4.ran || g4.value.minimallyComplete !== false) return null;
  const missing: DetailsWordingId[] = [];
  if (subject.assessedValue === null) missing.push("details.required.assessedValue");
  if (ownerEntered && (subject.parcel?.value ?? "").trim() === "") missing.push("details.required.parcel");
  if (missing.length === 0) {
    missing.push(g4.reason === "parcelMissing" ? "details.required.parcel" : "details.required.assessedValue");
  }
  const named = missing.map((id) => words(id));
  const keys = missing.map((id) => id.replace("details.required.", "")).join(" ");
  if (named.length === 1) {
    return { id: "details.required.one", text: wordsWith("details.required.one", { field: sentenceCased(named[0]) }), missing: keys };
  }
  const last = named[named.length - 1];
  return {
    id: "details.required.two",
    text: wordsWith("details.required.two", { fields: sentenceCased(named.slice(0, -1).join(", ")), last }),
    missing: keys,
  };
}

export function sentenceCased(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}

// `RoomCountOptions` (RoomCountOptions.swift:18-56): 1–14 then "15+", baths in halves, and the current value too when it
// is none of those, so a record's 18 bedrooms or 2.75 baths stays visible and confirmable as it is.
export const TOP = 15;

export function bedroomOptions(current: number | null): number[] {
  const options = Array.from({ length: TOP }, (_, i) => i + 1);
  if (current !== null && Number.isFinite(current) && !options.includes(current)) options.push(current);
  return options.sort((a, b) => a - b);
}

export function bathroomOptions(current: number | null): number[] {
  const options = Array.from({ length: (TOP - 1) * 2 + 1 }, (_, i) => 1 + i / 2);
  if (current !== null && Number.isFinite(current) && !options.includes(current)) options.push(current);
  return options.sort((a, b) => a - b);
}

/** RoomCountOptions.swift:44-46. */
export function bedroomLabel(n: number): string {
  return n === TOP ? `${n}+` : `${n}`;
}

/** RoomCountOptions.swift:50-56: whole numbers bare, halves as they are, a stored quarter as it is. */
export function bathroomLabel(n: number): string {
  if (n === TOP) return `${Math.trunc(n)}+`;
  if (Number.isInteger(n)) return `${n}`;
  return n.toFixed(2).replace(/0+$/, "");
}

export function formatNumber(n: number, plain: boolean): string {
  return plain ? String(n) : n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/** What the owner typed, as a number: "$118,600" and "2,100" read as they look. Empty is null; anything else unreadable
 *  is "invalid", and the field goes back to what it showed. */
export function parseNumber(text: string, whole: boolean): number | null | "invalid" {
  const cleaned = text.replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  if (!/^\d+(\.\d+)?$/.test(cleaned)) return "invalid";
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return "invalid";
  return whole ? Math.round(n) : n;
}

// ═══ WEB-PARITY-2A ═════════════════════════════════════════════════════════════════════════════════════════════════
//
// WHICH of the app's cards and lines shows is read off the SERVER's answer — the record's standing, NEW-7, P-1's
// diagnosis, V-5, the deadline's kind — never re-judged here. What remains here is the app's own composition of its
// sentences: which fragment follows which, and how a figure is written into it.

export type Standing = NonNullable<SubjectView["record"]>;

/** What became of the county record (`SubjectView.record`), or null on a subject stored before 2.15.0. */
export function standingOf(subject: SubjectView): Standing | null {
  return subject.record ?? null;
}

/** THE OWNER-ENTERED PATH, as the server says it since 2.15.0: nothing of the record is on the subject, and not because a
 *  question is waiting (the app shows the map, and asks for no parcel, while it holds a record — SPV:1055, :915). */
export function ownerEnteredOf(subject: SubjectView, decisions: Decisions): boolean {
  const s = standingOf(subject);
  return s === null ? factsAreOwnerEntered(subject, decisions) : s.standing === "notAdopted";
}

// ── formatting, as the app writes each figure ────────────────────────────────────────────────────────────────────

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** `SubjectProperty.percentString` (Models.swift:1180): to the hundredth only where the ratio needs it. */
export function percentString(ratio: number): string {
  const hundredths = swiftRounded(ratio * 100 * 100) / 100;
  if (hundredths === swiftRounded(hundredths)) return `${Math.trunc(hundredths)}%`;
  const tenths = swiftRounded(hundredths * 10) / 10;
  return tenths === hundredths ? `${hundredths.toFixed(1)}%` : `${hundredths.toFixed(2)}%`;
}

/** Swift's `.rounded()`: half away from zero. */
export function swiftRounded(x: number): number {
  return x < 0 ? -Math.round(-x) : Math.round(x);
}

/** `SubjectProperty.monthYearPhrase` (Models.swift:638): a provider's `yyyyMMdd` as "August 2024", or null. */
export function monthYearPhrase(raw: string): string | null {
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 6) return null;
  const month = Number(digits.slice(4, 6));
  if (!(month >= 1 && month <= 12)) return null;
  return `${MONTHS[month - 1]} ${digits.slice(0, 4)}`;
}

/** `friendlySaleDate` (SubjectPropertyView.swift:2759): "Aug 2024"; the year alone for an unreadable month. */
export function friendlySaleDate(raw: string): string | null {
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 6) return null;
  const month = Number(digits.slice(4, 6));
  if (!(month >= 1 && month <= 12)) return digits.slice(0, 4);
  return `${MONTHS[month - 1].slice(0, 3)} ${digits.slice(0, 4)}`;
}

/** An ISO day as the app's `DateFormatter` template "MMMMd" writes it: "August 1". */
export function monthDay(iso: string): string {
  const [, month, day] = iso.split("-");
  return `${MONTHS[Number(month) - 1] ?? month} ${Number(day)}`;
}

// ── the record the owner is asked about (DT-29, DT-30) ──────────────────────────────────────────────────────────

/** `unitPickerExplanation` (SubjectPropertyView.swift:2656-2668), over the server's parcels. */
export function unitExplanation(standing: Standing, unit: string | null): { id: DetailsWordingId; text: string } {
  const u = (unit ?? "").trim();
  const named = u === "" ? words("details.unit.named.none") : wordsWith("details.unit.named", { unit: u });
  const tail = words("details.unit.tail");
  const rows = standing.unitCandidates;
  if (rows.length === 0) {
    return { id: "details.unit.matched", text: wordsWith("details.unit.matched", { address: standing.recordAddress ?? "", named, tail }) };
  }
  const labelled = rows.filter((c) => (c.unitNumber ?? "").trim() !== "").length;
  if (labelled === 0) {
    return { id: "details.unit.unlabelled", text: wordsWith("details.unit.unlabelled", { count: rows.length, named, tail }) };
  }
  return { id: "details.unit.labelled", text: wordsWith("details.unit.labelled", { count: rows.length, labelled, named, tail }) };
}

/** `unitChoiceCaption` (:2683-2690). */
export function unitCaption(standing: Standing): string {
  const base = words("details.unit.caption");
  const shown = standing.unitCandidates.length;
  if (standing.unitCandidatesTruncated) return wordsWith("details.unit.caption.truncated", { base, shown });
  return shown > 6 ? wordsWith("details.unit.caption.all", { base, shown }) : base;
}

type Candidate = Standing["unitCandidates"][number];

/** `UnitCandidate.matches(filter:)` (PropertyDataProvider.swift:48-66): digits only on both sides, so "$81,400" and
 *  "81400" are the same query — the unit label, the parcel, whole-dollar figures, the year built and the sale date. */
export function candidateMatches(c: Candidate, filter: string): boolean {
  const digits = (s: string): string => s.replace(/\D/g, "");
  const q = digits(filter);
  if (q === "") return true;
  const haystacks: string[] = [];
  if (c.unitNumber !== null) haystacks.push(digits(c.unitNumber));
  if (c.parcelId !== null) haystacks.push(digits(c.parcelId));
  for (const v of [c.assessedValue?.amount ?? null, c.lastSalePrice?.amount ?? null, c.sqft]) {
    if (v !== null && v > 0) haystacks.push(String(swiftRounded(v)));
  }
  if (c.yearBuilt !== null) haystacks.push(String(c.yearBuilt));
  if (c.lastSaleDate !== null) haystacks.push(digits(c.lastSaleDate));
  return haystacks.some((h) => h.includes(q));
}

/** `unitChoiceDetail` (:2749-2758): "318 sq ft · 1 bath · built 1900", only the parts the record has. */
export function unitDetail(c: Candidate): string | null {
  const parts: string[] = [];
  if (c.sqft !== null && c.sqft > 0) parts.push(wordsWith("details.unit.row.sqft", { sqft: Math.trunc(c.sqft) }));
  if (c.beds !== null && c.beds > 0) parts.push(wordsWith("details.unit.row.beds", { beds: c.beds, s: c.beds === 1 ? "" : "s" }));
  if (c.baths !== null && c.baths > 0) {
    const n = Number.isInteger(c.baths) ? String(c.baths) : c.baths.toFixed(1);
    parts.push(wordsWith("details.unit.row.baths", { baths: n, s: c.baths === 1 ? "" : "s" }));
  }
  if (c.yearBuilt !== null && c.yearBuilt > 1500) parts.push(wordsWith("details.unit.row.built", { year: c.yearBuilt }));
  return parts.length === 0 ? null : parts.join(" · ");
}

/** `unitChoiceSale` (:2762-2770): "Bought Aug 2024 for $79,900". */
export function unitSale(c: Candidate): string | null {
  const when = c.lastSaleDate === null ? null : friendlySaleDate(c.lastSaleDate);
  const price = c.lastSalePrice === null ? null : money(c.lastSalePrice);
  if (when !== null && price !== null) return wordsWith("details.unit.row.bought", { when, price });
  if (when !== null) return wordsWith("details.unit.row.lastSold", { when });
  if (price !== null) return wordsWith("details.unit.row.lastSoldFor", { price });
  return null;
}

export function unitAssessed(c: Candidate): string {
  return c.assessedValue === null
    ? words("details.unit.row.noAssessed")
    : wordsWith("details.unit.row.assessed", { value: money(c.assessedValue) });
}

/** `unitChoiceConfirmation` (:2784-2794): the row's facts again, one to a line, and why it matters. */
export function unitConfirmation(c: Candidate): string {
  const parts: string[] = [];
  const u = (c.unitNumber ?? "").trim();
  if (u !== "") parts.push(wordsWith("details.unit.row.unit", { unit: u }));
  if (c.assessedValue !== null) parts.push(wordsWith("details.unit.row.assessed", { value: money(c.assessedValue) }));
  const detail = unitDetail(c);
  if (detail !== null) parts.push(detail);
  const sale = unitSale(c);
  if (sale !== null) parts.push(sale);
  const p = (c.parcelId ?? "").trim();
  if (p !== "") parts.push(wordsWith("details.unit.row.parcel", { parcel: p }));
  const facts = parts.length === 0 ? words("details.unit.confirm.this") : parts.join("\n");
  return wordsWith("details.unit.confirm.message", { facts });
}

/** `sentenceList` (:2855-2861). */
function sentenceList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return wordsWith("details.unit.list.two", { a: items[0], b: items[1] });
  return wordsWith("details.unit.list.two", { a: items.slice(0, -1).join(", "), b: items[items.length - 1] });
}

/** THE FACTS THE COUNTY DOES NOT CARRY FOR THE PARCEL THE OWNER PICKED (`missingFacts`, :2863-2870, shown at :2299-2307):
 *  read off the subject — a fact the chosen parcel had none of is absent, or the owner's with no county figure beside it. */
export function unitGaps(subject: SubjectView): string | null {
  const standing = standingOf(subject);
  if (standing === null || standing.standing !== "adopted" || standing.unitChoice === null) return null;
  const lacking = (fact: Fact<number>): boolean =>
    fact === null || fact === undefined || !(fact.value > 0) || (fact.source === "ownerCorrected" && fact.recordValue == null);
  const gaps: DetailsWordingId[] = [];
  if (lacking(subject.sqft)) gaps.push("details.unit.gap.sqft");
  if (lacking(subject.beds)) gaps.push("details.unit.gap.beds");
  if (lacking(subject.baths)) gaps.push("details.unit.gap.baths");
  if (lacking(subject.yearBuilt)) gaps.push("details.unit.gap.year");
  if (gaps.length === 0) return null;
  const one = gaps.length === 1;
  return wordsWith("details.unit.gaps", {
    list: sentenceList(gaps.map((id) => words(id))),
    pronoun: words(one ? "details.unit.gaps.it" : "details.unit.gaps.them"),
    Pronoun: words(one ? "details.unit.gaps.It" : "details.unit.gaps.They"),
  });
}

// ── the manual path (DT-28) ─────────────────────────────────────────────────────────────────────────────────────

/** `refusalExplanation(_:typed:)` (:1091-1112), by I-2's own reason. */
export function refusalExplanation(i2: Decisions["I-2"], typed: string): { id: DetailsWordingId; text: string } {
  const address = typed.trim() === "" ? words("details.refusal.thatAddress") : typed.trim();
  if (!i2.ran) return { id: "details.refusal.noResolvedAddress", text: wordsWith("details.refusal.noResolvedAddress", { address }) };
  const reason = i2.reason ?? "";
  const mismatch = i2.value.mismatch;
  const id: DetailsWordingId = reason === "droppedSubUnit"
    ? "details.refusal.droppedSubUnit"
    : mismatch === "houseNumber" ? "details.refusal.houseNumber"
      : mismatch === "street" ? "details.refusal.street"
        : mismatch === "directional" ? "details.refusal.directional"
          : "details.refusal.noResolvedAddress";
  return { id, text: wordsWith(id, { address }) };
}

/** `showPartialRecordsNote` (:414-444): the record was adopted and came back partial, or without the assessed value. */
export function partialRecords(subject: SubjectView): { titleId: DetailsWordingId; bodyId: DetailsWordingId; body: string } | null {
  if (standingOf(subject)?.standing !== "adopted") return null;
  const needsValue = subject.assessedValue === null;
  const missingKey = !((subject.sqft?.value ?? 0) > 0) || subject.beds === null || !((subject.baths?.value ?? 0) > 0)
    || subject.yearBuilt === null;
  if (!needsValue && !missingKey) return null;
  if (!needsValue) return { titleId: "details.partial.filled", bodyId: "details.partial.body", body: words("details.partial.body") };
  const action = words("details.partial.action.1") + words("details.partial.action.2") + words("details.partial.action.3")
    + words("details.partial.action.4");
  // `PropertyTypeSupport.isCondo` on the owner's type — the same predicate `unitIsRequired` reads.
  const condo = subject.propertyType?.value === "Condo";
  const lead = condo
    ? words("details.partial.condo.1") + words("details.partial.condo.2") + words("details.partial.condo.3")
    : words("details.partial.house");
  return { titleId: "details.partial.needsValue", bodyId: condo ? "details.partial.condo.1" : "details.partial.house", body: lead + action };
}

// ── the recent purchase (DT-31) ─────────────────────────────────────────────────────────────────────────────────

/** `purchaseContradictionSentence` (:840-852): NEW-7's own two figures, and the date of the sale it read. */
export function purchaseSentence(new7: { purchase: Money<"market">; impliedMarket: Money<"market"> }, saleDate: string | null): string {
  const month = saleDate === null ? null : monthYearPhrase(saleDate);
  const when = month === null ? "" : wordsWith("details.purchase.when", { month });
  return wordsWith("details.purchase.paid", { purchase: money(new7.purchase), when })
    + wordsWith("details.purchase.implies", { implied: money(new7.impliedMarket) })
    + words("details.purchase.against.1") + words("details.purchase.against.2");
}

export function canRaiseSentenceId(risk: "cannotRaiseAtBoard" | "canRaise" | "deNovoRisk" | "unknown"): DetailsWordingId {
  return `details.purchase.risk.${risk}`;
}

// ── the low figure, and the prompt (DT-33, F-WP1C-4) ─────────────────────────────────────────────────────────────

type Diagnosis = { banner: "delaware" | "vintage" | "taxBill" | "fractional" | "general"; alert: "taxBill" | "vintage" | "general" };

/** P-1's diagnosis, or the app's general line where the server sent none (a subject stored before 2.15.0). */
export function diagnosisOf(p1: Decisions["P-1"]): Diagnosis {
  return p1.ran && p1.value.diagnosis !== undefined ? p1.value.diagnosis : { banner: "general", alert: "general" };
}

/** `lowValueMessage` (:368-387), by the server's diagnosis. */
export function lowValueLine(d: Diagnosis, assessed: Money<"assessed"> | null): { id: DetailsWordingId; text: string } {
  if (d.banner === "taxBill") {
    const shown = assessed === null ? words("details.low.taxBill.none") : money(assessed);
    return { id: "details.low.taxBill", text: wordsWith("details.low.taxBill", { shown }) };
  }
  const id: DetailsWordingId = `details.low.${d.banner}`;
  return { id, text: words(id) };
}

/** `valueBlockTitle` and `valueBlockMessage` (:394-404), by the server's diagnosis. */
export function p1Prompt(d: Diagnosis, assessed: Money<"assessed"> | null): {
  titleId: DetailsWordingId; messageId: DetailsWordingId; message: string;
} {
  const shown = assessed === null ? words("details.alert.p1.none") : money(assessed);
  const messageId: DetailsWordingId = `details.alert.p1.message.${d.alert}`;
  return {
    titleId: d.alert === "taxBill" ? "details.alert.p1.taxBill.title" : "details.alert.p1.title",
    messageId,
    message: wordsWith(messageId, { shown }),
  };
}

// ── the ratio from the owner's notice (DT-15) ────────────────────────────────────────────────────────────────────

/** The percent the owner's ratio shows as (`localRatioPercent`'s getter, SubjectPropertyView.swift:1938): hundredths. */
export function ratioPercentShown(ratio: number): number {
  return swiftRounded(ratio * 10_000) / 100;
}

/**
 * THE LINE UNDER THE RATIO FIELD (`assessmentRatioCard`, :2037-2084), in the app's order: the owner's ratio far from the
 * record's market figure (the server's check), else the owner's ratio in force, else the county's expired or applied
 * figure, else the plain words for a ratio nobody has. `pa` names the STEB CLR, the one state whose words this website
 * serves; the county's figure is V-5's, the jurisdiction's own.
 */
export function ratioStatus(args: {
  jurisdiction: { value: number; effectiveTo: string | null };
  inForce: { value: number; origin: string };
  check: SubjectView["ratioCheck"];
  pa: boolean;
  today: string;
}): { id: DetailsWordingId; text: string; tone: "warn" | "good" | "plain" } {
  const { jurisdiction, inForce, check, pa, today } = args;
  if (check !== null && check !== undefined) {
    return {
      id: "details.ratio.check",
      text: wordsWith("details.ratio.check", { implied: money(check.impliedMarket), onFile: money(check.onFile) }),
      tone: "warn",
    };
  }
  if (inForce.origin === "ownerEntered") {
    return { id: "details.ratio.entered", text: wordsWith("details.ratio.entered", { pct: percentString(inForce.value) }), tone: "good" };
  }
  const hasPrefill = jurisdiction.value < 0.999;
  if (hasPrefill && pa) {
    const term = words("details.ratio.term.pa");
    const pct = percentString(jurisdiction.value);
    // `RatioFreshness.of(effectiveTo:asOf:)` — on the client, as ratio.ts says: a missing date is unknown, never expired.
    const expired = jurisdiction.effectiveTo !== null && jurisdiction.effectiveTo < today;
    if (expired) {
      const on = wordsWith("details.ratio.expired.on", { date: longDate(jurisdiction.effectiveTo) });
      return { id: "details.ratio.expired", text: wordsWith("details.ratio.expired", { term, pct, on }), tone: "warn" };
    }
    return { id: "details.ratio.applied", text: wordsWith("details.ratio.applied", { term, pct }), tone: "good" };
  }
  return { id: "details.ratio.generic", text: words("details.ratio.generic"), tone: "plain" };
}

// ── the deadline, in the app's words that promise no reminder (DT-23, F-EM-9) ─────────────────────────────────────

/** WEBSITE REMINDERS DO NOT EXIST YET (EMAIL_PLAN, F-EM-9), so no sentence promising one may render. The one switch. */
export const WEBSITE_REMINDERS_LIVE = false;

type Deadline = NonNullable<ResolvePropertyResponse["body"]["jurisdiction"]["deadline"]>;

/** The day the served deadline states, when its kind lets one be printed (`ReminderService.printableDeadline`, R122). */
export function printableDeadlineDay(deadline: Deadline | null): string | null {
  if (deadline === null) return null;
  switch (deadline.kind) {
    case "fixedStatutory": return deadline.date.date;
    case "noticeDrivenFloor": return deadline.floor.date;
    case "countyVariant":
      return deadline.resolved.at === "county" ? deadline.resolved.date.date
        : deadline.resolved.at === "stateFloor" ? deadline.resolved.floor.date : null;
    default: return null;
  }
}

/**
 * WHAT SHOWS UNDER "Filing deadline" WHEN THE OWNER HAS ENTERED NO DATE. The app shows its estimate note there
 * (`deadlineEstimateNote`, ReminderService.swift:816-842), and every branch of it is a reminder sentence. So this is the
 * app's own non-reminder wording for the same fact — the Review screen's "Deadline" row (ReviewView.swift:661-662): the
 * printable day, "around August 1 — our estimate, confirm the exact date on your notice". A deadline with no printable
 * day says the server's own sentence for it (the backstop's, Tyler's, which carries no date), and an unverified one says
 * nothing, as the app says nothing it can stand behind there.
 */
export function deadlineNote(deadline: Deadline | null): { kind: "estimate"; text: string } | { kind: "copy"; copy: Copy } | null {
  const day = printableDeadlineDay(deadline);
  if (day !== null) {
    return { kind: "estimate", text: wordsWith("details.deadline.around", { date: monthDay(day) }) + words("details.deadline.estimate") };
  }
  if (deadline === null || deadline.kind === "unverified") return null;
  return { kind: "copy", copy: deadline.notice };
}
